import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Spinner,
  Button,
  Row,
  Col,
  Form,
  Modal,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import SmartPagination from "../Report/EmailCampaignRunReport/DynamicSmartPagination";
import {
  smartConnectViewAssignedDids,
  unAssignedAgentCurrentDid,
} from "../../store/slices/smartConnect/smartConnectDids";
import { ICONS } from "../../constants/icons";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "../../layouts/default";
import { hasPermission } from "../../services/utils";
import { PERMISSIONS } from "../../contants/permission";

const LIMIT = 10;

function SmartConnectAssignDidsView() {
  const { group_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDid, setSelectedDid] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [didFilter, setDidFilter] = useState("");
  const [appliedDidFilter, setAppliedDidFilter] = useState("");

  const { viewDids } = useSelector((store) => store.smartConnectDIDsReducer);

  const { data = [], pagination = {}, loading } = viewDids || {};
  const isAssignedToday = (assignedAt) => {
    if (!assignedAt) return false;

    const assignedDate = new Date(assignedAt);
    const today = new Date();

    return (
      assignedDate.getDate() === today.getDate() &&
      assignedDate.getMonth() === today.getMonth() &&
      assignedDate.getFullYear() === today.getFullYear()
    );
  };

  const {
    cq_agent_id,
    smart_connect_agent_id,
    agent_type,
    agent_name,
    assign_date,
  } = location.state || {};

  useEffect(() => {
    if (!cq_agent_id || !smart_connect_agent_id || !agent_type) {
      toast.error("Missing agent context. Please try again.");
      navigate(-1);
      return;
    }

    dispatch(
      smartConnectViewAssignedDids({
        cq_agent_id,
        smart_connect_agent_id,
        agent_type,
        limit: LIMIT,
        page,
        did_number: appliedDidFilter || undefined,
        status: "active",
      }),
    );
  }, [
    cq_agent_id,
    smart_connect_agent_id,
    agent_type,
    assign_date,
    page,
    appliedDidFilter,
    dispatch,
    navigate,
  ]);

  const handleBack = () => navigate(-1);

  const handleDeleteDid = (did) => {
    setSelectedDid(did);
    setDeleteReason("");
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteReason.trim()) {
      toast.error("Please provide a reason to deactivate this DID.");
      return;
    }

    try {
      setSubmitting(true);

      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.data?.id;

      if (!userId) {
        toast.error("User Id not found. Please Re-Login");
        return;
      }

      if (!group_id) {
        toast.error("group_id is missing. pls refresh and try again later.");
        return;
      }

      const payload = {
        agent: selectedDid,
        userId,
        reason: deleteReason,
        agent_type: "DYNAMIC_GROUP",
        agent_group_id: group_id,
      };

      const msg = await dispatch(unAssignedAgentCurrentDid(payload)).unwrap();
      toast.success(msg || "DID deactivated successfully.");

      setShowDeleteModal(false);
      setSelectedDid(null);

      //REFRESH DATA AFTER DELETE
      dispatch(
        smartConnectViewAssignedDids({
          cq_agent_id,
          smart_connect_agent_id,
          agent_type,
          limit: LIMIT,
          page,
          did_number: appliedDidFilter || undefined,
          status: "active",
        }),
      );
    } catch (err) {
      toast.error(err || err?.message || "Failed to deactivate DID");
    } finally {
      setSubmitting(false);
    }
  };

  const showValue = (value) => value ?? "-";

  const handleReset = () => {
    setDidFilter("");
    setAppliedDidFilter("");
    setPage(1);
  };

  return (
    <Layout>
      {/* Header */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <Button variant="secondary" onClick={handleBack}>
          Back
        </Button>

        <div className="text-end">
          <h3 className="mb-0">Dynamic Group Agent DIDs</h3>
        </div>
      </div>

      {/* Agent Info */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col md={3}>
              <strong>Agent Name</strong>
              <div>{showValue(agent_name)}</div>
            </Col>
            <Col md={3}>
              <strong>CQ Agent ID</strong>
              <div>{showValue(cq_agent_id)}</div>
            </Col>
            <Col md={3}>
              <strong>Smart Connect ID</strong>
              <div>{showValue(smart_connect_agent_id)}</div>
            </Col>
            <Col md={3}>
              <strong>Agent Type</strong>
              <div>{showValue(agent_type)}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Filter */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end g-2">
            <Col md={4}>
              <Form.Control
                placeholder="Search DID number"
                value={didFilter}
                onChange={(e) => setDidFilter(e.target.value)}
              />
            </Col>

            <Col md="auto">
              <Button
                onClick={() => {
                  setPage(1);
                  setAppliedDidFilter(didFilter);
                }}
              >
                Search
              </Button>
            </Col>

            <Col md="auto">
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card>
        <Card.Body>
          <Table bordered hover responsive>
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>DID Number</th>
                <th>DAYS TO SPAM</th>
                <th>DAYS TO SHUFFLE</th>
                {/* <th>ASSIGNED AT</th> */}
                {hasPermission(PERMISSIONS.SMART_CONNECT.EDIT) &&
                  hasPermission(PERMISSIONS.SMART_CONNECT.DELETE) && (
                    <th>ACTION</th>
                  )}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    <Spinner size="sm" />
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No DIDs found
                  </td>
                </tr>
              ) : (
                data.map((did, index) => (
                  <tr key={did.id || index}>
                    <td>{(page - 1) * LIMIT + index + 1}</td>
                    <td>{showValue(did.did_number)}</td>
                    <td>{showValue(did.days_to_spam)}</td>
                    <td>{showValue(did.days_to_shuffle)}</td>
                    {/* <td>
                                            {did.assigned_at
                                                ? new Date(did.assigned_at).toLocaleString("en-IN", {
                                                    timeZone: "Asia/Kolkata",
                                                })
                                                : "-"}
                                        </td> */}
                    {hasPermission(PERMISSIONS.SMART_CONNECT.EDIT) &&
                      hasPermission(PERMISSIONS.SMART_CONNECT.DELETE) && (
                        <td>
                          {
                            <Button
                              variant="link"
                              className="p-0"
                              title="Spam DID"
                              onClick={() => handleDeleteDid(did)}
                            >
                              <img
                                src={ICONS.delete_action}
                                alt="Delete"
                                style={{ width: 25, opacity: 0.8 }}
                              />
                            </Button>
                          }
                        </td>
                      )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Modal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            centered
            backdrop="static"
          >
            <Modal.Header closeButton>
              <Modal.Title>Deactivate DID</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <p className="mb-2">
                Are you sure you want to deactivate this DID?
              </p>

              <div className="mb-2">
                <strong>DID:</strong> {selectedDid?.did_number || "-"}
              </div>

              <Form.Group>
                <Form.Label className="fw-semibold">
                  Reason to deactivate <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter reason for deactivating this DID"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                />
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>

              <Button
                variant="danger"
                onClick={handleConfirmDelete}
                disabled={submitting}
              >
                {submitting ? "Deactivating..." : "Deactivate DID"}
              </Button>
            </Modal.Footer>
          </Modal>

          {pagination?.totalPages > 1 && (
            <SmartPagination
              currentPage={page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          )}
        </Card.Body>
      </Card>
    </Layout>
  );
}

export default SmartConnectAssignDidsView;
